const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const SessionCalendar = require('@/lib/Sessions/SessionCalendar');
const { toYM } = require('@/functions/date');

class SessionCalendarPopulator {
    /**
     * @param {CartService} cartService
     * @param {SessionAvailabilityService} sessionAvailabilityService
     * @param {SessionRepository} sessionRepository
     */
    constructor(
        cartService,
        sessionAvailabilityService,
        sessionRepository,
    ) {
        /**
         * @type {CartService}
         */
        this.cartService = cartService;

        /**
         * @type {SessionAvailabilityService}
         */
        this.sessionAvailabilityService = sessionAvailabilityService;

        /**
         * @type {SessionRepository}
         */
        this.sessionRepository = sessionRepository;

        /**
         * @type {ConsoleLogger}
         */
        this.log = logFactory('SessionCalendarPopulator');
    }

    /**
     * Returns a new empty SessionCalendar.
     *
     * @param {Number} eventID
     * @param {Function?} [sessionFilter]
     *
     * @return {SessionCalendar}
     */
    create(eventID, sessionFilter = null) {
        return new SessionCalendar(
            eventID,
            sessionFilter
        );
    }

    /**
     * Populate the supplied calendar with sessions for the supplied month.
     * The sessions for this month might already be loaded, or already be loading. In this case null will be returned.
     * Otherwise, a promise will be returned which resolves after the sessions have been loaded from IndexedDB and
     * availability has been loaded (or an error loading availability happened).
     *
     * @param {SessionCalendar} calendar
     * @param {Date} date
     * @param {ItemRequest[]} requestedTickets
     * @param {SessionSpaceCalculator} sessionSpaceCalculator
     * @param {number} branchID
     *
     * @return {?Promise<string>} Returns a promise with a string value YYYY-MM, or null.
     */
    loadMonth(calendar, date, requestedTickets, sessionSpaceCalculator, branchID) {
        const month = toYM(date);

        if (
            calendar.monthLoadingPromises.hasOwnProperty(month)
            && (
                calendar.monthLoadingPromises[month].sessionsPromise
                || calendar.monthLoadingPromises[month].availabilityPromise
            )
        ) {
            // Already loaded (or still loading) sessions for this month.
            return null;
        }

        return this
            .addSessionsForMonth(calendar, month, branchID)
            .then((sessions) => {
                // Set the bookable space counts in the calendar first using the space
                // counts that were stored in the local cache (IndexedDB).
                this.setBookableSpacesForSessions(sessions, calendar, sessionSpaceCalculator);

                return this.requestAvailabilityForMonth(calendar, date, requestedTickets, sessions)
                    .catch(() => {
                        // Suppress errors loading availability because the device might be offline.
                        // This should not break showing remaining spaces because the cached numbers
                        // should have already been set (above).
                    })
                    .then(() => {
                        // The bookable resource availability endpoint also returns
                        // updated space numbers for all the sessions.
                        // Use these new numbers to update the bookable space counts in the calendar.
                        this.setBookableSpacesForSessions(sessions, calendar, sessionSpaceCalculator);

                        return month;
                    });
            });
    }

    /**
     *
     * @param {Session[]} sessions
     * @param {SessionCalendar} calendar
     * @param {SessionSpaceCalculator} sessionSpaceCalculator
     */
    setBookableSpacesForSessions(sessions, calendar, sessionSpaceCalculator) {
        sessions.forEach((session) => {
            calendar.setBookableSpacesForSession(
                session.ID,
                sessionSpaceCalculator.getBookableSpacesForSession(session)
            );
        });
    }

    /**
     * @private
     *
     * @param {SessionCalendar} calendar
     * @param {string} month Month in YYYY-MM format.
     * @param {number} branchID
     *
     * @return {Promise<Session[] | Session[]>|Promise<Object<string, Session[]>>}
     */
    addSessionsForMonth(calendar, month, branchID) {
        const startedAt = (new Date()).getTime();
        this.log.debug(`Loading sessions for event ${calendar.eventID} for month ${month}`);
        ++calendar.monthLoadingCount;

        const promise = this.sessionRepository.getSessionsForEventForMonth(calendar.eventID, month, branchID)
            .then((sessions) => {
                calendar.addSessions(sessions);

                this.log.debug(
                    `Loaded ${sessions.length} session(s) for ${month} in ${((new Date()).getTime() - startedAt)} ms.`
                );

                return sessions;
            });

        promise.finally(() => {
            --calendar.monthLoadingCount;
        });

        calendar.setLoadingPromise(month, promise, undefined);

        return promise;
    }

    /**
     * @private
     *
     * Makes an API request to the /sessionavailability endpoint with a big list of sessionIDs and requested itemIDs
     * to determine which sessions are available, based on bookable resource availability.
     *
     * This does nothing to prevent making the same request for the same sessions many times, which is why it is
     * marked as private and should not be used outside this class.
     *
     * @param {SessionCalendar} calendar
     * @param {Date} date
     * @param {ItemRequest[]} requestedTickets
     * @param {Session[]} [sessions] Sessions can be passed in to save loading them from the calendar.
     */
    requestAvailabilityForMonth(calendar, date, requestedTickets, sessions) {
        const month = toYM(date);

        if (calendar.monthLoadingPromises.hasOwnProperty(month) && calendar.monthLoadingPromises[month].availabilityPromise) {
            // Already loaded (or still loading) availability for this month.
            return calendar.monthLoadingPromises[month].availabilityPromise;
        }

        if (sessions === undefined) {
            sessions = calendar.getSessionsForMonth(date);
        }

        if (sessions.length < 1) {
            return Promise.resolve();
        }

        const startedAt = (new Date()).getTime();
        this.log.debug(`Loading availability for ${sessions.length} session(s) for ${month}.`);
        ++calendar.monthLoadingCount;

        const promise = this.sessionAvailabilityService
            .loadSessionAvailabilities(
                sessions,
                requestedTickets.map((r) => {
                    return {
                        qty: r.qty,
                        itemID: r.item.ID,
                    };
                }),
                this.cartService.currentCart.getOriginalOrderID()
            )
            .then(async (sessionAvailabilities) => {
                // Update the space properties stored in the session in IndexedDB with the latest
                // number returned by the API.
                await this.sessionRepository.setSpacesOfSessions(sessions, sessionAvailabilities);

                // Create a new object instead of using Object.assign on the existing as recommended by Vue:
                // https://vuejs.org/v2/guide/reactivity.html#For-Objects
                calendar.availability = Object.assign(
                    {},
                    calendar.availability,
                    sessionAvailabilities
                );

                sessions.forEach((session) => {
                    if (calendar.availability.hasOwnProperty(session.ID)) {
                        // If the availability response said the session is not "suitable" or "available"
                        // with regards to bookable resources, set it as having no capacity.
                        if (
                            !calendar.availability[session.ID].bookableResourcesAvailable
                            || !calendar.availability[session.ID].bookableResourcesSuitable
                        ) {
                            calendar.setSessionUnavailable(session.ID);
                        }
                    }
                });

                this.log.debug(
                    `Loaded availability for ${sessions.length} sessions for ${month} in ${((new Date()).getTime() - startedAt)} ms.`
                );

                return sessions;
            });

        promise.finally(() => {
            --calendar.monthLoadingCount;
        });

        calendar.setLoadingPromise(month, undefined, promise);

        return promise;
    }
}

module.exports = SessionCalendarPopulator;
